import { el, clear, toast, fmtRemaining } from '../../dom';
import { fieldApi, type Grave } from '../../game-api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { ApiError } from '../../../core/network/api';
import { richText } from '../../ui/icons';
import { refreshBag, type FarmCtx } from './context';
import { playSfx } from '../../audio/audio';

/**
 * Gravestone recovery.
 *
 * Dying drops 30% of your coins and half of one resource stack into a gravestone
 * (field.service `die`) that expires in ten minutes. The whole mechanic existed
 * server-side with NO client at all — `/field/graves` and the claim endpoint had
 * zero callers — so a death was simply a permanent, unexplained loss of coins and
 * materials. This is the missing half: a banner that appears while an unclaimed
 * grave stands, counts down the time you have left, and hands the stuff back.
 *
 * The grave is written at the RESPAWN tile, not where you fell (the server has no
 * map, and dungeon/arena deaths happen in scenes whose coordinates mean nothing
 * here) — so there is nowhere to walk to and no proximity gate to honour. The
 * banner IS the marker.
 */
export interface GraveTracker {
  /** Re-read the server's list. Call on farm load and right after a death. */
  refresh: () => void;
  destroy: () => void;
}

/** "12 coins · 8 wood" — what the stone is holding, skipping the empty halves. */
export function graveSpoils(g: Pick<Grave, 'coins' | 'resource' | 'amount'>): string {
  const parts: string[] = [];
  if (g.coins > 0) parts.push(`${g.coins} 🪙`);
  if (g.resource && g.amount > 0) parts.push(`${g.amount} ${g.resource}`);
  return parts.join(' · ');
}

/** A grave plus the local deadline we derive from its remaining-ms. */
type LiveGrave = Grave & { expiresAt: number };

/**
 * The live tracker, so ANY death site can ask for a re-read without threading a
 * handle through it (resources.ts kills you from a swan, farm.ts from a mob or
 * poison). Same pattern as context.ts's bag painter.
 */
let live: GraveTracker | null = null;
export const refreshGraves = (): void => live?.refresh();

export function createGraveTracker(host: HTMLElement, c: FarmCtx): GraveTracker {
  // Sits under the top-right action cluster rather than over the vitals column,
  // which is the one bit of chrome a player watches while being killed.
  const bar = el('div', 'tb-hud-graves game-font absolute top-20 right-3 z-30 hidden flex-col gap-1 items-end max-w-[70vw]');
  host.appendChild(bar);

  let graves: LiveGrave[] = [];
  let alive = true;
  // Re-render once a second only to move the countdowns — the list itself only
  // changes on refresh(), so this never issues a request.
  const tick = window.setInterval(() => { if (graves.length) paint(); }, 1000);

  function paint() {
    // Drop anything that crumbled away while the banner was on screen, so the
    // player never clicks Recover on a stone the server has already forgotten.
    const now = Date.now();
    graves = graves.filter((g) => g.expiresAt > now);
    clear(bar);
    bar.classList.toggle('hidden', graves.length === 0);
    bar.classList.toggle('flex', graves.length > 0);
    for (const g of graves) {
      const row = el('div', 'flex items-center gap-2 px-3 py-2 rounded-lg bg-surface/90 border border-error/60 shadow-lift backdrop-blur-sm');
      row.append(el('div', 'flex flex-col', {},
        el('div', 'text-[12px] text-error inline-flex items-center gap-1', {}, // "gravestone" is already the noun; the tombstone emoji had no sprite and was
        // silently dropped, leaving a leading dash.
        ...richText(`Your gravestone — ${graveSpoils(g) || 'empty'}`)),
        el('div', 'text-[11px] text-text-dim font-mono', {}, `crumbles in ${fmtRemaining(g.expiresAt - now)}`)));
      row.append(el('button',
        'px-2.5 py-1 rounded border border-gold text-gold text-sm hover:bg-gold/10',
        { onClick: () => void claim(g) }, 'Recover'));
      bar.append(row);
    }
  }

  async function claim(g: LiveGrave) {
    try {
      const r = await dispatchAction('field.claimGrave', (id) => fieldApi.claimGrave(g.id, id), { key: g.id });
      playSfx('coin_purchase');
      toast(`Recovered ${graveSpoils({ coins: r.coins, resource: r.resource, amount: r.amount }) || 'nothing'}`);
      graves = graves.filter((x) => x.id !== g.id);
      paint();
      refreshBag(c);                       // a resource stack came back — repaint the hotbar
      await c.game.refreshHud();           // …and the coins came back to the wallet
    } catch (e) {
      if (isDropped(e)) return;
      toast(e instanceof ApiError ? e.message : 'Could not recover the grave', 'error');
      refresh();                           // already claimed / crumbled → resync the list
    }
  }

  function refresh() {
    void fieldApi.graves()
      .then((list) => {
        if (!alive) return;
        // The server answers with a REMAINING duration; turn it into a deadline
        // once, here, so the per-second repaint doesn't drift with render lag.
        const now = Date.now();
        graves = list.map((g) => ({ ...g, expiresAt: now + g.expiresInMs }));
        paint();
      })
      .catch(() => undefined);             // a failed poll must never break the farm
  }

  const handle: GraveTracker = {
    refresh,
    destroy: () => { alive = false; if (live === handle) live = null; window.clearInterval(tick); bar.remove(); },
  };
  live = handle;
  return handle;
}
